import React, { useMemo } from 'react';
import { OrderStatusHistoryItem, OrderStatus } from '../../../../api/shop/types/orderTypes';
import { getImageUrl } from '../../../../utils/imageUrl';
import { BADGE_COLORS } from '../../../../Common/BadgeStyles';
import './TrackOrder.css';

interface TrackingStep {
  status: string;
  title: string;
  number?: string;
}

interface TrackOrderProps {
  orderStatusHistory: OrderStatusHistoryItem[];
  currentStatus: OrderStatus | string;
  expectedPickupDate?: string | null;
  title?: string;
  trackingSteps?: TrackingStep[];
  refundStatus?: string | null; // For returns: refund status (INITIATED, PROCESSED, etc.)
}

const TrackOrder: React.FC<TrackOrderProps> = ({ 
  orderStatusHistory, 
  currentStatus, 
  expectedPickupDate,
  title = 'Track Order',
  trackingSteps: customTrackingSteps,
  refundStatus
}) => {
  // Memoize image URLs to prevent recalculation on every render
  const completedIconUrl = useMemo(() => getImageUrl('admin/static-assets/icons/shop-service/check-step-indicator.svg'), []);
  const currentIconUrl = useMemo(() => getImageUrl('admin/static-assets/icons/general/stepIndicator.svg'), []);
  const pendingIconUrl = useMemo(() => getImageUrl('admin/static-assets/icons/shop-service/number-indicator.svg'), []);

  const formatTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      });
    } catch {
      return '';
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric' 
      });
    } catch {
      return '';
    }
  };

  // Define the tracking steps - use custom steps if provided, otherwise use default order steps
  const trackingSteps: TrackingStep[] = customTrackingSteps || [
    {
      status: 'CONFIRMED',
      title: 'Order Placed',
      number: '01'
    },
    {
      status: 'READY_FOR_PICKUP',
      title: 'Ready for pickup',
      number: '02'
    },
    {
      status: 'PICKED_UP',
      title: 'Order delivered',
    }
  ];

  // Determine current step based on current status
  const getCurrentStepIndex = () => {
    // Use custom status order if provided, otherwise use default
    const statusOrder = customTrackingSteps 
      ? customTrackingSteps.map(step => step.status)
      : ['CONFIRMED', 'READY_FOR_PICKUP', 'PICKED_UP'];
    
    let index = statusOrder.indexOf(currentStatus);
    
    // Handle DELIVERED status (treat as same as PICKED_UP) - only for order tracking
    if (!customTrackingSteps && currentStatus === 'DELIVERED') {
      index = 2; // Same as PICKED_UP index
    }
    
    return index >= 0 ? index : 0; // Default to 0 if status not found
  };

  const currentStepIndex = getCurrentStepIndex();

  // Calculate progress line percentage to reach circle edges exactly
  const getProgressPercentage = () => {
    // For custom tracking (returns and cancellations)
    if (customTrackingSteps) {
      const stepCount = customTrackingSteps.length;
      
      // If REFUNDED, all steps are completed (100%)
      if (currentStatus === 'REFUNDED') {
        return 100;
      }
      
      // If REJECTED (2-step flow), line reaches step 1 (REJECTED step) - full line complete
      if (currentStatus === 'REJECTED') {
        return 100;
      }
      
      // If REFUND_INITIATED (3-step or 4-step flow), line reaches refund step - full line complete
      if (currentStatus === 'REFUND_INITIATED') {
        return 100;
      }
      
      // Handle cancellation flow (4 steps: Order Placed, Cancel Requested, Cancel Approved/Rejected, Refund Initiated)
      if (stepCount === 4 && (currentStatus === 'REQUESTED' || currentStatus === 'APPROVED' || currentStatus === 'REJECTED')) {
        if (currentStepIndex === 0) return 25; // Order Placed: line reaches 25%
        if (currentStepIndex === 1) return 50; // Cancel Requested: line reaches 50%
        if (currentStepIndex === 2) return 100; // Cancel Approved/Rejected: full line complete
        return 0;
      }
      
      // For 2-step flow (REJECTED), handle differently
      if (stepCount === 2) {
        if (currentStepIndex === 0) return 50; // REQUESTED: line reaches halfway to step 2
        if (currentStepIndex === 1) return 100; // REJECTED: full line complete
        return 0;
      }
      
      // For 3-step flow (normal), use standard calculation
      if (stepCount === 3) {
        if (currentStepIndex === 0) return 50; // REQUESTED: line reaches circle 2
        if (currentStepIndex === 1) return 100; // APPROVED: full line complete
        return 0;
      }
      
      // Fallback
      if (currentStepIndex === 0) return 50;
      if (currentStepIndex === 1) return 100;
      return 0;
    }
    
    // Original order tracking logic
    // Line width = calc(66.67% - 32px) spans from first to last circle
    // Circle 2 left edge distance from line start = calc(33.33% - 32px)
    // Progress = (33.33% - 32px) / (66.67% - 32px) = 50%
    
    if (currentStepIndex === 0) {
      // CONFIRMED: green line to left edge of circle 2
      return 50; // Exactly half the line to reach circle 2
    }
    if (currentStepIndex === 1) {
      // READY_FOR_PICKUP: green line reaches circle 3 (full line)
      return 100;
    }
    if (currentStepIndex === 2 || currentStatus === 'DELIVERED') {
      // PICKED_UP or DELIVERED: full line complete
      return 100;
    }
    return 0; // Default case
  };

  const getStatusInfo = (status: string) => {
    return orderStatusHistory.find(item => item.status === status);
  };

  const getStepIcon = (stepIndex: number) => {
    const stepStatus = getStepStatus(stepIndex);
    const step = trackingSteps[stepIndex];
    
    // For custom tracking (returns and cancellations), use stepStatus to determine icon
    if (customTrackingSteps) {
      if (stepStatus === 'completed') {
        return (
          <img 
            src={completedIconUrl}
            alt="Completed" 
            className="track-step-icon completed"
            loading="lazy"
          />
        );
      }
      
      if (stepStatus === 'current') {
        return (
          <img 
            src={currentIconUrl}
            alt="Current Step" 
            className="track-step-icon current"
            loading="lazy"
          />
        );
      }
      
      const isReturn = trackingSteps.length > 0 && trackingSteps[0]?.status === 'REQUESTED';
      const isCancellation4thStep = stepIndex === 3;
      
      if (isReturn || isCancellation4thStep) {
        return (
          <div className="track-step-icon-container track-step-number-only">
            {step.number && (
              <span className="track-step-number-overlay">
                {step.number}
              </span>
            )}
          </div>
        );
      }
      
      return (
        <div className="track-step-icon-container">
          <img 
            src={pendingIconUrl}
            alt={`Step ${stepIndex + 1}`} 
            className="track-step-icon pending"
            loading="lazy"
          />
          {step.number && (
            <span className="track-step-number-overlay">
              {step.number}
            </span>
          )}
        </div>
      );
    }
    
    // Original order tracking logic
    // For DELIVERED or PICKED_UP status: all steps are completed (show green checkmarks)
    if (currentStatus === 'DELIVERED' || currentStatus === 'PICKED_UP') {
      return (
        <img 
          src={completedIconUrl}
          alt="Completed" 
          className="track-step-icon completed"
          loading="lazy"
        />
      );
    }
    
    if (currentStatus === 'READY_FOR_PICKUP') {
      if (stepIndex <= 1) {
        return (
          <img 
            src={completedIconUrl}
            alt="Completed" 
            className="track-step-icon completed"
            loading="lazy"
          />
        );
      } else if (stepIndex === 2) {
        return (
          <img 
            src={currentIconUrl}
            alt="Current Step" 
            className="track-step-icon current"
            loading="lazy"
          />
        );
      }
    }
    
    if (currentStatus === 'CONFIRMED') {
      if (stepIndex === 0) {
        return (
          <img 
            src={completedIconUrl}
            alt="Completed" 
            className="track-step-icon completed"
            loading="lazy"
          />
        );
      } else if (stepIndex === 1) {
        return (
          <img 
            src={currentIconUrl}
            alt="Current Step" 
            className="track-step-icon current"
            loading="lazy"
          />
        );
      } else if (stepIndex === 2) {
        return (
          <div className="track-step-icon-container">
            <img 
              src={pendingIconUrl}
              alt={`Step ${stepIndex + 1}`} 
              className="track-step-icon pending"
              loading="lazy"
            />
            {step.number && (
              <span className="track-step-number-overlay">
                {step.number}
              </span>
            )}
          </div>
        );
      }
    }
    
    return (
      <div className="track-step-icon-container">
        <img 
          src={pendingIconUrl}
          alt={`Step ${stepIndex + 1}`} 
          className="track-step-icon pending"
          loading="lazy"
        />
        {step.number && (
          <span className="track-step-number-overlay">
            {step.number}
          </span>
        )}
      </div>
    );
  };

  const getStepStatus = (stepIndex: number) => {
    const step = trackingSteps[stepIndex];
    const statusInfo = getStatusInfo(step.status);
    
    // Check if step has been completed (exists in statusHistory with updatedAt/date)
    const isStepCompleted = statusInfo && (statusInfo.updatedAt !== null && statusInfo.updatedAt !== undefined);
    
    // For custom tracking (returns), check statusHistory to determine completion
    if (customTrackingSteps) {
      // If REFUNDED (processed/completed), all steps are completed
      if (currentStatus === 'REFUNDED') {
        return 'completed';
      }
      
      // If REJECTED, handle rejected status (only for returns, not cancellations)
      // Cancellations have CONFIRMED at stepIndex 0, so check for that to distinguish
      if (currentStatus === 'REJECTED') {
        // Check if this is a return (stepIndex 0 is REQUESTED) or cancellation (stepIndex 0 is CONFIRMED)
        const isReturn = trackingSteps.length > 0 && trackingSteps[0]?.status === 'REQUESTED';
        
        // For returns: Step 0 (REQUESTED) is completed, Step 1 (REJECTED) is completed (rejection is final)
        if (isReturn) {
          if (stepIndex === 0) {
            return 'completed';
          }
          if (step.status === 'REJECTED') {
            return 'completed'; // REJECTED step is completed once rejection is done
          }
          return 'pending';
        }
        // For cancellations: Let it fall through to cancellation handling below (line 336)
        // Don't return early, let the cancellation logic handle it
      }
      
      // If REFUND_INITIATED, handle refund initiated status
      // For cancellations (4-step flow), step 2 is APPROVED, step 3 is REFUND_INITIATED
      // For returns (3-step flow), step 2 is REFUNDED
      if (currentStatus === 'REFUND_INITIATED') {
        // Check if this is a cancellation (stepIndex 0 is CONFIRMED) or return (stepIndex 0 is REQUESTED)
        const isCancellation = trackingSteps.length > 0 && trackingSteps[0]?.status === 'CONFIRMED';
        
        if (isCancellation) {
          // For cancellations: Steps before REFUND_INITIATED (stepIndex 3) are completed
          if (stepIndex < 3) {
            return 'completed';
          }
          // Step 3 (REFUND_INITIATED) is current
          if (step.status === 'REFUND_INITIATED' || step.status === 'REFUND_PROCESSED') {
            return 'current';
          }
          return 'pending';
        } else {
          // For returns: Step 2 (REFUNDED) is current when refund is initiated
          if (step.status === 'REFUNDED') {
            return 'current';
          }
          // Steps before REFUNDED are completed
          if (stepIndex < 2) {
            return 'completed';
          }
          return 'pending';
        }
      }
      
      // Handle cancellation tracking steps - Use REQUESTED, APPROVED, REJECTED, REFUND_INITIATED
      if (currentStatus === 'REQUESTED' || currentStatus === 'APPROVED' || currentStatus === 'REJECTED' || currentStatus === 'REFUND_INITIATED' || currentStatus === 'REFUNDED') {
        const refundStatusUpper = refundStatus?.toUpperCase();
        
        // Step 1: Order Placed (CONFIRMED) - always completed
        if (step.status === 'CONFIRMED' && stepIndex === 0) {
          return 'completed';
        }
        
        // Step 2: Cancel Requested (REQUESTED)
        if (step.status === 'REQUESTED' && stepIndex === 1) {
          // If status is REQUESTED, APPROVED, or REJECTED, the REQUESTED step is completed
          if (currentStatus === 'REQUESTED' || currentStatus === 'APPROVED' || currentStatus === 'REJECTED' || currentStatus === 'REFUND_INITIATED' || currentStatus === 'REFUNDED') {
            return 'completed'; // REQUESTED step is completed once cancellation is requested
          }
          return 'current';
        }
        
        // Step 3: Cancel Approved/Rejected
        if (step.status === 'APPROVED' && stepIndex === 2) {
          if (currentStatus === 'APPROVED' || currentStatus === 'REFUND_INITIATED' || currentStatus === 'REFUNDED') {
            return 'completed'; // APPROVED step is completed
          }
          // If status is still REQUESTED, show APPROVED step as current (blue mark)
          if (currentStatus === 'REQUESTED') {
            return 'current';
          }
          return 'pending';
        }
        
        if (step.status === 'REJECTED' && stepIndex === 2) {
          if (currentStatus === 'REJECTED') {
            return 'completed'; // REJECTED step is completed once rejection is done
          }
          return 'pending';
        }
        
        // Step 4: Refund Initiated - Only show if status is APPROVED (not REJECTED)
        if ((step.status === 'REFUND_INITIATED' || step.status === 'REFUND_PROCESSED') && stepIndex === 3) {
          // Don't show refund step if cancellation is rejected
          if (currentStatus === 'REJECTED') {
            return 'pending'; // Hide refund step for rejected cancellations
          }
          // Check refund status first
          if (refundStatusUpper === 'PROCESSED' || refundStatusUpper === 'COMPLETED' || refundStatusUpper === 'SUCCESS') {
            return 'completed'; // Refund is processed
          } else if (refundStatusUpper === 'INITIATED' || currentStatus === 'REFUND_INITIATED') {
            return 'current'; // Refund is in progress
          } else {
            return 'pending'; // Refund not yet initiated
          }
        }
      }
      
      // Legacy cancellation handling (for backward compatibility)
      if (currentStatus === 'CANCELLED') {
        // For cancellations, check refund status to determine step 3 state
        const refundStatusUpper = refundStatus?.toUpperCase();
        
        if (step.status === 'CONFIRMED' && stepIndex === 0) {
          return 'completed'; // Order Placed step is always completed for cancellations
        }
        
        if (step.status === 'CANCELLED' && stepIndex === 1) {
          return 'completed'; // Order Cancelled step is completed
        }
        
        if ((step.status === 'REFUND_PROCESSED' || step.status === 'REFUND_INITIATED') && stepIndex === 2) {
          if (refundStatusUpper === 'PROCESSED') {
            return 'completed'; // Refund is processed
          } else if (refundStatusUpper === 'INITIATED') {
            return 'current'; // Refund is in progress
          } else {
            return 'pending'; // Refund not yet initiated
          }
        }
      }
      
      // If step has updatedAt/date in statusHistory, it's completed (e.g., REQUESTED with date)
      if (isStepCompleted) {
        return 'completed';
      }
      
      const currentStepIndex = getCurrentStepIndex();
      
      // Special handling for APPROVED status: Don't auto-advance to REFUNDED step
      // REFUNDED step should only be "current" when refund is actually initiated
      if (currentStatus === 'APPROVED') {
        // If this is the REFUNDED step and no refund has been initiated, it should be pending
        if (step.status === 'REFUNDED') {
          // Check if refund has been initiated by checking refundStatus prop
          const refundStatusUpper = refundStatus?.toUpperCase();
          if (!refundStatusUpper || refundStatusUpper === '') {
            return 'pending'; // No refund initiated yet, show as pending
          }
          // If refund is initiated, it will be handled by REFUND_INITIATED check above
        }
        // For other steps when APPROVED, use normal logic
        if (stepIndex < currentStepIndex) {
          return 'completed';
        }
        if (step.status === currentStatus) {
          return 'completed'; // APPROVED step itself is completed
        }
        return 'pending';
      }
      
      // If previous step is completed, check if we should advance
      if (stepIndex > 0) {
        const prevStep = trackingSteps[stepIndex - 1];
        const prevStatusInfo = getStatusInfo(prevStep.status);
        const prevStepCompleted = prevStatusInfo && (prevStatusInfo.updatedAt !== null && prevStatusInfo.updatedAt !== undefined);
        
        // Only advance to next step if it's not REFUNDED step OR if refund is initiated
        if (prevStepCompleted) {
          // If this is REFUNDED step, only make it current if refund is initiated
          if (step.status === 'REFUNDED') {
            const refundStatusUpper = refundStatus?.toUpperCase();
            if (refundStatusUpper && refundStatusUpper !== '') {
              // Refund is initiated, will be handled by REFUND_INITIATED status check above
              // This fallback ensures it's at least current
              return 'current';
            }
            // No refund initiated, keep it pending
            return 'pending';
          }
          // For non-REFUNDED steps, advance normally
          return 'current';
        }
      }
      
      // If current step matches currentStatus, it's current
      if (step.status === currentStatus) {
        return 'current';
      }
      
      // If step is before current step index, it should be completed
      if (stepIndex < currentStepIndex) {
        return 'completed';
      }
      
      // Otherwise, it's pending
      return 'pending';
    }
    
    // Original order tracking logic
    // For DELIVERED or PICKED_UP: all steps completed
    if (currentStatus === 'DELIVERED' || currentStatus === 'PICKED_UP') {
      return 'completed';
    }
    
    // For READY_FOR_PICKUP: steps 0 & 1 completed, step 2 current
    if (currentStatus === 'READY_FOR_PICKUP') {
      if (stepIndex <= 1) return 'completed';
      if (stepIndex === 2) return 'current';
      return 'pending';
    }
    
    // For CONFIRMED: step 0 completed, step 1 current, step 2 pending
    if (currentStatus === 'CONFIRMED') {
      if (stepIndex === 0) return 'completed';
      if (stepIndex === 1) return 'current';
      if (stepIndex === 2) return 'pending';
    }
    
    // Default fallback
    return 'pending';
  };

  const getStepSubtitle = (stepIndex: number, stepStatus: string) => {
    // For return tracking and cancellation tracking: show actual refund status when refund step is current
    if (customTrackingSteps && stepStatus === 'current') {
      const step = trackingSteps[stepIndex];
      
      // Check for REFUNDED step (returns) or REFUND_PROCESSED/REFUND_INITIATED step (cancellations)
      if (step?.status === 'REFUNDED' || step?.status === 'REFUND_PROCESSED' || step?.status === 'REFUND_INITIATED') {
        const refundStatusUpper = refundStatus?.toUpperCase();
        if (refundStatusUpper) {
          // Map refund statuses to display text
          if (refundStatusUpper === 'INITIATED') {
            return 'Initiated';
          }
          if (refundStatusUpper === 'PROCESSED') {
            return 'Processed';
          }
          if (refundStatusUpper === 'COMPLETED' || refundStatusUpper === 'SUCCESS') {
            return 'Completed';
          }
          if (refundStatusUpper === 'FAILED') {
            return 'Failed';
          }
          // Fallback: show the status as-is (capitalize first letter)
          return refundStatusUpper.charAt(0) + refundStatusUpper.slice(1).toLowerCase();
        }
      }
      
      // If step is REJECTED and current, show "Rejected"
      if (step?.status === 'REJECTED' || currentStatus === 'REJECTED') {
        return 'Rejected';
      }
    }
    
    // Also handle cancellation refund status when step is completed
    if (customTrackingSteps && stepStatus === 'completed') {
      const step = trackingSteps[stepIndex];
      
      // For cancellation refund step that's completed, show "Processed" instead of "Completed"
      if ((step?.status === 'REFUND_PROCESSED' || step?.status === 'REFUND_INITIATED') && currentStatus === 'CANCELLED') {
        const refundStatusUpper = refundStatus?.toUpperCase();
        if (refundStatusUpper === 'PROCESSED') {
          return 'Processed';
        }
      }
    }
    
    switch (stepStatus) {
      case 'completed':
        return 'Completed';
      case 'current':
        return 'In Progress';
      case 'pending':
        return 'Pending';
      default:
        return 'Pending';
    }
  };

  // Get badge colors from centralized BADGE_COLORS - single source of truth
  const getBadgeColors = (stepStatus: string, stepIndex?: number) => {
    // For return tracking and cancellation tracking: show badge colors based on refund status when refund step is current
    if (customTrackingSteps && stepStatus === 'current') {
      const step = trackingSteps[stepIndex || 0];
      
      // Check for REFUNDED step (returns) or REFUND_PROCESSED/REFUND_INITIATED step (cancellations)
      if (step?.status === 'REFUNDED' || step?.status === 'REFUND_PROCESSED' || step?.status === 'REFUND_INITIATED') {
        const refundStatusUpper = refundStatus?.toUpperCase();
        if (refundStatusUpper) {
          if (refundStatusUpper === 'INITIATED') {
            return BADGE_COLORS.FULFILLMENT.PICKUP; // Orange badge for initiated
          }
          if (refundStatusUpper === 'PROCESSED') {
            return BADGE_COLORS.SELECTED_COLORS; // Blue badge for processed
          }
          if (refundStatusUpper === 'COMPLETED' || refundStatusUpper === 'SUCCESS') {
            return BADGE_COLORS.STATUS.ACTIVE; // Green badge for completed
          }
          if (refundStatusUpper === 'FAILED') {
            return BADGE_COLORS.STATUS.INACTIVE; // Red badge for failed
          }
        }
      }
      
      // If step is REJECTED and current, use red/inactive colors
      if (step?.status === 'REJECTED' || currentStatus === 'REJECTED') {
        return BADGE_COLORS.STATUS.INACTIVE; // Red badge for rejected
      }
    }
    
    // Also handle cancellation refund status when step is completed
    if (customTrackingSteps && stepStatus === 'completed') {
      const step = trackingSteps[stepIndex || 0];
      
      // For cancellation refund step that's completed, use green badge
      if ((step?.status === 'REFUND_PROCESSED' || step?.status === 'REFUND_INITIATED') && currentStatus === 'CANCELLED') {
        const refundStatusUpper = refundStatus?.toUpperCase();
        if (refundStatusUpper === 'PROCESSED') {
          return BADGE_COLORS.STATUS.ACTIVE; // Green badge for processed
        }
      }
    }
    
    switch (stepStatus) {
      case 'completed':
        // Use same colors as STATUS.ACTIVE (green theme)
        return BADGE_COLORS.STATUS.ACTIVE;
      case 'current':
        // Use same colors as SELECTED_COLORS (blue theme for in-progress)
        return BADGE_COLORS.SELECTED_COLORS;
      case 'pending':
        // Use same colors as FULFILLMENT.PICKUP (orange theme for pending)
        return BADGE_COLORS.FULFILLMENT.PICKUP;
      default:
        // Default fallback - use inactive colors
        return BADGE_COLORS.STATUS.INACTIVE;
    }
  };

  // Calculate line positioning based on step count
  const getLineStyles = () => {
    if (!customTrackingSteps) {
      // Default 3-step positioning
      return {
        left: 'calc(16.67% + 16px)',
        width: 'calc(66.67% - 32px)'
      };
    }
    
    const stepCount = customTrackingSteps.length;
    
    if (stepCount === 2) {
      // For 2 steps: line spans from right edge of step 1 to left edge of step 2
      // Step 1 center: ~25%, Step 2 center: ~75% (with flex: 1 and padding)
      return {
        left: 'calc(25% + 16px)', // Start from right edge of first circle
        width: 'calc(50% - 32px)' // Span to left edge of second circle
      };
    }
    
    // Default 3-step positioning
    return {
      left: 'calc(16.67% + 16px)',
      width: 'calc(66.67% - 32px)'
    };
  };

  const lineStyles = getLineStyles();

  return (
    <div className="track-order-container">
      <h3 className="track-order-title">{title}</h3>
      
      <div className="track-order-progress">
        {/* Background progress line */}
        <div 
          className="track-progress-background"
          style={{
            left: lineStyles.left,
            width: lineStyles.width
          }}
        >
          <div className="track-progress-line"></div>
          <div 
            className="track-progress-line-completed" 
            style={{ 
              width: `${getProgressPercentage()}%`
            }}
          ></div>
        </div>
        
        {trackingSteps.map((step, index) => {
          const statusInfo = getStatusInfo(step.status);
          const stepStatus = getStepStatus(index);
          
          return (
            <div key={step.status} className={`track-step ${stepStatus}`}>
              {/* Step Indicator */}
              <div className="track-step-indicator">
                <div className="track-step-circle">
                  {getStepIcon(index)}
                </div>
              </div>
              
              {/* Step Content */}
              <div className="track-step-content">
                <h4 className="track-step-title">{step.title}</h4>
                <div className="track-step-details">
                  {statusInfo?.updatedAt ? (
                    <>
                      <span className="track-step-time">
                        {formatTime(statusInfo.updatedAt)} • {formatDate(statusInfo.updatedAt)}
                      </span>
                      <span 
                        className="track-step-status"
                        style={{
                          backgroundColor: getBadgeColors(stepStatus, index).backgroundColor,
                          color: getBadgeColors(stepStatus, index).color,
                          border: `1px solid ${getBadgeColors(stepStatus, index).borderColor || getBadgeColors(stepStatus, index).color}`,
                        }}
                      >
                        {getStepSubtitle(index, stepStatus)}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="track-step-expected">
                        {(step.status === 'READY_FOR_PICKUP' || step.status === 'PICKED_UP') && expectedPickupDate && 
                          `Expected pick up on ${formatDate(expectedPickupDate)}`
                        }
                      </span>
                      <span 
                        className="track-step-status"
                        style={{
                          backgroundColor: getBadgeColors(stepStatus, index).backgroundColor,
                          color: getBadgeColors(stepStatus, index).color,
                          border: `1px solid ${getBadgeColors(stepStatus, index).borderColor || getBadgeColors(stepStatus, index).color}`,
                        }}
                      >
                        {getStepSubtitle(index, stepStatus)}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TrackOrder;